// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { InstrumentIdentifier } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Types';
import { MintRequest } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Mint';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { InstrumentSelect } from '../../../components/InstrumentSelect';
import { useParties } from '../../../contexts/PartiesContext';
import { useRequestMint } from '../../../hooks/mutations/registry/mint/useRequestMint';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
};

const isPositiveNumber = (value: string) => {
  if (Number.isNaN(parseFloat(value))) {
    return 'Ensure value is a number';
  }
  if (parseFloat(value) < 0) {
    return 'Ensure value is positive';
  }
  return '';
};

export const RequestMintDialog = ({ open, onClose }: DialogProps) => {
  const [instrumentId, setInstrumentId] = useState<string>('');
  const { operator, party } = useParties();
  const [amount, setAmount] = useState<string>('');
  const [label, setLabel] = useState<string>('');
  const [registrar, setRegistrar] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [amountValidationMsg, setAmountValidationMsg] = useState<string>('');
  const requestMint = useRequestMint();
  const resetState = useCallback(() => {
    setInstrumentId('');
    setAmount('');
    setLabel('');
    setRegistrar('');
    setReference('');
  }, [setInstrumentId, setAmount, setLabel, setRegistrar, setReference]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    const payload: MintRequest = {
      mint: {
        operator,
        provider: party,
        holder: party,
        registrar,
        instrumentIdentifier: {
          source: registrar,
          id: instrumentId,
          scheme: 'RegistrarInternalScheme',
        } as InstrumentIdentifier,
        amount,
        reference,
        batch: { id: uuidv4(), size: '1', settlementFrom: null, settlementUntil: null },
      },
      holdingLabel: label,
    };
    await requestMint({ argument: payload });
    handleClose();
  }, [
    operator,
    party,
    label,
    amount,
    instrumentId,
    registrar,
    reference,
    requestMint,
    handleClose,
  ]);
  const onInstrumentChange = (instId: string, instRegistrar: string) => {
    setInstrumentId(instId);
    setRegistrar(instRegistrar);
  };

  const disableSubmit = !instrumentId || !amount || !registrar;
  return (
    <FormDialog
      open={open}
      title="Request Mint"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <InstrumentSelect instrumentId={instrumentId} onSelect={onInstrumentChange} />
      <TextInput
        setValidationMessage={setAmountValidationMsg}
        getValidationMessage={isPositiveNumber}
        validationMessage={amountValidationMsg}
        label="Amount"
        value={amount}
        setValue={setAmount}
        required
      />
      <TextInput label="Label" value={label} setValue={setLabel} hidden />
      <TextInput label="Registrar" value={registrar} setValue={setRegistrar} required />
      <TextInput label="Reference" value={reference} setValue={setReference} />
    </FormDialog>
  );
};
